import { useEffect, useState } from "react";

import {
  TextField,
  Checkbox,
  TableStateWithFilters,
  Select,
  TableMetadata,
  useTable,
  Label,
  ArrowButton,
  ColumnSortEnum,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, PagedResult } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import {
  getSchoolCandidateInstitutionAdminFilteredList,
  getSchoolCandidateInstitutionAdminListExport,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  SearchCard,
  InstitutionSelect,
  ListTable,
} from "../../components";
import { useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { InstitutionCodeBookSearch, Role } from "../../models";
import { RequestType } from "../../models/Request";
import {
  SchoolCandidateInstitutionAdminSearch,
  SchoolCandidateInstitutionAdmin,
  SchoolCandidateStatus,
} from "../../models/SchoolCandidate";
import { SchoolCandidateInstitutionAdminSort } from "../../models/SchoolCandidate/SchoolCandidateInstitutionAdminSort";
import { ClaimType } from "../../models/User";
import { schoolCandidateInstitutionAdminDetailUrl } from "../../routes/routes";
import { setRedirectURL } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils, TableUtils } from "../../utils";

export function SchoolCandidateInstitutionAdminList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  //STORAGE KEY
  const storageKey = `SchoolCandidateInstitutionAdminSearchTableState`;
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<SchoolCandidateInstitutionAdminSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        SchoolCandidateInstitutionAdminSort.Child,
        ColumnSortEnum.ASC
      )
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<SchoolCandidateInstitutionAdminSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER
  const createInitialSchoolCandidateInstitutionAdminSearch =
    (): SchoolCandidateInstitutionAdminSearch => {
      return {
        institutionIds: initialTableState.institutionIds
          ? initialTableState.institutionIds
          : defaultInstitutionIds,
        childLastName: initialTableState.childLastName,
        childFirstName: initialTableState.childFirstName,
        childOib: initialTableState.childOib,
        status: initialTableState.status,
        requestType: initialTableState.requestType,
        onlyWithAddReasonDelayPreviousYear:
          initialTableState.onlyWithAddReasonDelayPreviousYear,
        onlyWithTemporaryAddress: initialTableState.onlyWithTemporaryAddress,
        requestNotSubmitted: initialTableState.requestNotSubmitted,
      };
    };
  const defaultInstitutionIds: number[] =
    (auth.currentRole?.locations?.length ?? 0) > 0 &&
    auth.currentRole?.locations &&
    auth.currentRole?.locations[0]
      ? [auth!.currentRole!.locations![0]]
      : ([] as number[]);

  const isFounderRole = auth.currentRole?.role === Role.Founder;

  //TABLE DATA
  const defaultTableData =
    TableUtils.tableDefaultData<SchoolCandidateInstitutionAdmin>();
  const [tableData, setTableData] =
    useState<PagedResult<SchoolCandidateInstitutionAdmin>>(defaultTableData);
  //SET SEARCH FILTERS
  const [
    schoolCandidateInstitutionAdminSearch,
    setSchoolCandidateInstitutionAdminSearch,
  ] = useState<SchoolCandidateInstitutionAdminSearch>(() =>
    createInitialSchoolCandidateInstitutionAdminSearch()
  );
  const [tableSearch, setTableSearch] =
    useState<SchoolCandidateInstitutionAdminSearch>(() =>
      createInitialSchoolCandidateInstitutionAdminSearch()
    );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<SchoolCandidateInstitutionAdmin> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "child",
        header: t("common.schoolCandidateModel.childData"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName} &nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.oib}
              </div>
            </div>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.Child,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div className={"text-nowrap text-right"}>
              {hrDate(item.dateOfBirth)}
            </div>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.DateOfBirth,
      },
      {
        columnKey: "permanentAndTemporaryAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.residence?.permanentAddress?.street}&nbsp;
                  {item.residence?.permanentAddress?.houseNumber},&nbsp;
                  {item.residence?.permanentAddress?.town}
                </b>
              </div>
              {item.residence?.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.residence?.temporaryAddress.street}&nbsp;
                  {item.residence?.temporaryAddress.houseNumber},&nbsp;
                  {item.residence?.temporaryAddress.town}
                </div>
              )}
            </>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.Address,
      },
      {
        columnKey: "delayTemporaryExemption",
        header:
          t("common.schoolCandidateModel.delay") +
          "/" +
          t("common.schoolCandidateModel.temporaryExemptionForPreviousYear"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div>{item.postponement ? t("common.yes") : t("common.no")}</div>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.DelayTemporaryExemption,
      },
      {
        columnKey: "isHandicapped",
        header: t("common.schoolCandidateModel.isHandicapped"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div className={"text-nowrap"}>
              {item.isHandicapped ? t("common.yes") : t("common.no")}
            </div>
          );
        },
      },
      {
        columnKey: "status",
        header: t("common.schoolCandidateModel.schoolCandidateStatus"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          const status = statuses.find((v) => v.id === item.status);
          return (
            <Label
              color={FormatUtils.getSchoolCandidateStatusColor(status!.id)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {status?.name}
            </Label>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.Status,
      },
      {
        columnKey: "requestType",
        header: t("common.schoolCandidateModel.requestType"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          if (item.requestType !== null) {
            const requestType = requestTypes.find(
              (v) => v.id === item.requestType
            );
            return (
              <Label
                color={FormatUtils.getSchoolCandidateStatusColor(
                  requestType!.id
                )}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                {requestType!.name}
              </Label>
            );
          } else {
            return (
              <Label
                color={FormatUtils.getSchoolCandidateStatusColor()}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                Nema zahtjeva
              </Label>
            );
          }
        },
        sortBy: SchoolCandidateInstitutionAdminSort.RequestType,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          if (isFounderRole) return null;

          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] =
    useTable<SchoolCandidateInstitutionAdminSearch>({
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: tableData!.rowCount,
      defaultSort: {
        order: initialTableState.order,
        sortBy: initialTableState.sortBy,
      },
    });
  //FILTER DATA
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(SchoolCandidateStatus, "schoolCandidateStatus")
  );
  const [requestTypes] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(RequestType, "requestType")
  );
  const [filteredInstitutions, setFilteredInstitutions] = useState<CodeBook[]>(
    [] as CodeBook[]
  );

  useEffect(() => {
    (async () => {
      if (auth.currentRole?.claimType === ClaimType.Institution) {
        if (auth.currentRole?.locations) {
          const filter: InstitutionCodeBookSearch = {
            onlyActive: false,
            search: undefined,
            ids: auth.currentRole.locations,
            includeOutsideEnrollmentArea: true,
            includeHandicapped: undefined, // both
            includePrivate: undefined, // both
            includeSatellite: undefined, // both
          };
          const result = await searchInstitutionNew(filter);
          setFilteredInstitutions(result);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolCandidateInstitutionAdminSearch]);

  useEffect(() => {
    (async () => {
      var request =
        TableUtils.GetSearchRequest<SchoolCandidateInstitutionAdminSearch>(
          tableSearch,
          tableState
        );
      var itemList = await getSchoolCandidateInstitutionAdminFilteredList(
        request
      );
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: string) => {
    dispatch(setRedirectURL(history.location.pathname));
    if (id) {
      TableUtils.SetSessionStorageItem<SchoolCandidateInstitutionAdminSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(
        schoolCandidateInstitutionAdminDetailUrl(Number(id.split("-")[0])!)
      );
    }
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getSchoolCandidateInstitutionAdminListExport(
      schoolCandidateInstitutionAdminSearch,
      exportType
    );
  };

  const handleInstitutionChange = (id: number) => {
    const newSchoolCandidateInstitutionAdminSearch: SchoolCandidateInstitutionAdminSearch =
      {
        ...schoolCandidateInstitutionAdminSearch,
        institutionIds: !isNaN(id) ? [id] : [],
      };
    setSchoolCandidateInstitutionAdminSearch(
      newSchoolCandidateInstitutionAdminSearch
    );
  };

  return (
    <ContentArea>
      <ToolbarTop pageTitle={t("navigation.schoolCandidateList")} />
      <SearchCard
        onClickSearch={() => {
          setTableSearch(schoolCandidateInstitutionAdminSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setSchoolCandidateInstitutionAdminSearch({
            institutionIds: defaultInstitutionIds,
            onlyWithAddReasonDelayPreviousYear: false,
            onlyWithTemporaryAddress: false,
            requestNotSubmitted: false,
          });
          setTableSearch({
            institutionIds: defaultInstitutionIds,
            onlyWithAddReasonDelayPreviousYear: false,
            onlyWithTemporaryAddress: false,
            requestNotSubmitted: false,
          });
          search();
        }}
        isFilterChanged={filterChanged}
        hideDivider={true}
      >
        <Row>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "institutionIds"
              )}
              value={
                schoolCandidateInstitutionAdminSearch.institutionIds &&
                schoolCandidateInstitutionAdminSearch.institutionIds.length > 0
                  ? schoolCandidateInstitutionAdminSearch.institutionIds[0]
                  : undefined
              }
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={false}
              showUser={true}
            />
          </Col>
          <Col md={2}></Col>
          <Col md={4}>
            <Select
              label={t("common.schoolCandidateModel.requestType")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "requestType"
              )}
              value={schoolCandidateInstitutionAdminSearch.requestType}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
              selectData={codeBookToSelectData(requestTypes)}
              emptyOption={{ value: undefined, text: t("common.all") }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.childLastName")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "childLastName"
              )}
              value={schoolCandidateInstitutionAdminSearch.childLastName}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.childFirstName")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "childFirstName"
              )}
              value={schoolCandidateInstitutionAdminSearch.childFirstName}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.childOib")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "childOib"
              )}
              value={schoolCandidateInstitutionAdminSearch.childOib}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={3}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateStatus")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "status"
              )}
              value={schoolCandidateInstitutionAdminSearch.status}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
              selectData={codeBookToSelectData(
                statuses.filter(
                  (status) =>
                    status.id !== SchoolCandidateStatus.notSent.valueOf()
                )
              )}
              emptyOption={{ value: undefined, text: t("common.all") }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t(
                "common.schoolCandidateModel.onlyWithAddReasonDelayPreviousYear"
              )}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "onlyWithAddReasonDelayPreviousYear"
              )}
              checked={
                schoolCandidateInstitutionAdminSearch.onlyWithAddReasonDelayPreviousYear
              }
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.onlyWithTemporaryAddress")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "onlyWithTemporaryAddress"
              )}
              checked={
                schoolCandidateInstitutionAdminSearch.onlyWithTemporaryAddress
              }
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.requestNotSubmitted")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "requestNotSubmitted"
              )}
              checked={
                schoolCandidateInstitutionAdminSearch.requestNotSubmitted
              }
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload={!isFounderRole}
      />
    </ContentArea>
  );
}
